import { CHANNEL_KIND } from '../catalog/channel';
import type { ChannelRef } from '../catalog/channel';
import type { LoadIconKey } from '../catalog/icon';
import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import type { ConfigIssue } from '../catalog/issue';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import type { LoadTypeKey } from '../catalog/loadType';
import type { LoadId, RoomId } from '../ids';
import { newLoadId } from '../ids';
import type { CommandResult, Load, RcuConfig } from '../model';
import { findLoad, findRail, findRoom, loadAtChannel } from '../query/lookup';
import { describeRef, isChannelInRange, targetFromRef } from '../query/targets';
import { fail } from '../result';
import { changedBy } from './outcome';

export interface LoadDraft {
  readonly name: string;
  readonly type: LoadTypeKey;
  readonly roomId: RoomId | null;
  readonly channel: ChannelRef | null;
  readonly wattage: number | null;
  /** null means "use the load type's default glyph". */
  readonly iconKey: LoadIconKey | null;
}

export interface LoadPatch {
  readonly name?: string;
  readonly type?: LoadTypeKey;
  readonly wattage?: number | null;
  readonly iconKey?: LoadIconKey;
  readonly roomId?: RoomId | null;
}

export interface BindOptions {
  /** Unwire whichever load currently occupies the output instead of refusing. */
  readonly replaceOccupant?: boolean;
}

const loadNotFound = (id: LoadId): ConfigIssue =>
  configIssue(ISSUE.LOAD_NOT_FOUND, { kind: ISSUE_TARGET.LOAD, id }, id);

const roomMissing = (id: RoomId): ConfigIssue =>
  configIssue(ISSUE.ROOM_NOT_FOUND, { kind: ISSUE_TARGET.ROOM, id }, id);

const nameMissing = (): ConfigIssue =>
  configIssue(ISSUE.NAME_REQUIRED, { kind: ISSUE_TARGET.LOAD }, 'Load');

/**
 * Everything that makes wiring `type` to `ref` impossible. `ignoreLoadId` is the
 * load being (re)wired, so its own existing wiring is not treated as a clash.
 */
const wiringIssue = (
  config: RcuConfig,
  ref: ChannelRef,
  type: LoadTypeKey,
  ignoreLoadId: LoadId | null,
  options: BindOptions,
): ConfigIssue | null => {
  const target = { kind: ISSUE_TARGET.LOAD, id: ignoreLoadId ?? undefined } as const;

  if (ref.kind === CHANNEL_KIND.DIN && findRail(config, ref.railId) === undefined) {
    return configIssue(ISSUE.UNKNOWN_RAIL, { kind: ISSUE_TARGET.RAIL, id: ref.railId }, ref.railId);
  }
  if (!isChannelInRange(config, ref)) {
    return configIssue(ISSUE.CHANNEL_OUT_OF_RANGE, target, describeRef(config, ref));
  }

  const resolved = targetFromRef(config, ref);
  if (resolved !== null && !resolved.acceptedLoadTypes.includes(type)) {
    return configIssue(
      ISSUE.INCOMPATIBLE_LOAD,
      target,
      `${LOAD_TYPE_SPECS[type].label} needs ${resolved.label} to accept it, but it drives ${resolved.outputHint} only`,
    );
  }

  const occupant = loadAtChannel(config, ref);
  if (occupant !== undefined && occupant.id !== ignoreLoadId && options.replaceOccupant !== true) {
    return configIssue(
      ISSUE.CHANNEL_OCCUPIED,
      target,
      `${describeRef(config, ref)} already drives ${occupant.name}`,
    );
  }

  return null;
};

export const addLoad = (
  config: RcuConfig,
  draft: LoadDraft,
  now: string,
): CommandResult<LoadId> => {
  if (draft.name.trim() === '') return fail(nameMissing());
  if (draft.roomId !== null && findRoom(config, draft.roomId) === undefined) {
    return fail(roomMissing(draft.roomId));
  }
  if (draft.channel !== null) {
    const issue = wiringIssue(config, draft.channel, draft.type, null, {});
    if (issue !== null) return fail(issue);
  }

  const load: Load = {
    id: newLoadId(),
    name: draft.name.trim(),
    type: draft.type,
    roomId: draft.roomId,
    channel: draft.channel,
    wattage: draft.wattage,
    iconKey: draft.iconKey ?? LOAD_TYPE_SPECS[draft.type].defaultIcon,
    createdAt: now,
  };
  return changedBy({ ...config, loads: [...config.loads, load] }, load.id, now);
};

export const updateLoad = (
  config: RcuConfig,
  id: LoadId,
  patch: LoadPatch,
  now: string,
): CommandResult<LoadId> => {
  const load = findLoad(config, id);
  if (load === undefined) return fail(loadNotFound(id));

  const name = patch.name === undefined ? load.name : patch.name.trim();
  if (name === '') return fail(nameMissing());

  if (patch.roomId !== undefined && patch.roomId !== null && findRoom(config, patch.roomId) === undefined) {
    return fail(roomMissing(patch.roomId));
  }

  const next: Load = { ...load, ...patch, name };

  // A load's type must stay compatible with whatever it is wired to.
  if (next.channel !== null) {
    const issue = wiringIssue(config, next.channel, next.type, id, { replaceOccupant: true });
    if (issue !== null) return fail(issue);
  }

  return changedBy(
    { ...config, loads: config.loads.map((current) => (current.id === id ? next : current)) },
    id,
    now,
  );
};

export const removeLoad = (config: RcuConfig, id: LoadId, now: string): CommandResult<LoadId> => {
  if (findLoad(config, id) === undefined) return fail(loadNotFound(id));
  return changedBy(
    { ...config, loads: config.loads.filter((load) => load.id !== id) },
    id,
    now,
  );
};

/** Wires a load to an output, optionally displacing whatever was already there. */
export const bindLoad = (
  config: RcuConfig,
  id: LoadId,
  ref: ChannelRef,
  options: BindOptions,
  now: string,
): CommandResult<Load | null> => {
  const load = findLoad(config, id);
  if (load === undefined) return fail(loadNotFound(id));

  const issue = wiringIssue(config, ref, load.type, id, options);
  if (issue !== null) return fail(issue);

  const occupant = loadAtChannel(config, ref);
  const displaced = occupant !== undefined && occupant.id !== id ? occupant : null;

  const loads = config.loads.map((current) => {
    if (displaced !== null && current.id === displaced.id) return { ...current, channel: null };
    if (current.id === id) return { ...current, channel: ref };
    return current;
  });

  return changedBy({ ...config, loads }, displaced, now);
};

export const unbindLoad = (config: RcuConfig, id: LoadId, now: string): CommandResult<LoadId> => {
  const load = findLoad(config, id);
  if (load === undefined) return fail(loadNotFound(id));
  return changedBy(
    {
      ...config,
      loads: config.loads.map((current) =>
        current.id === id ? { ...current, channel: null } : current,
      ),
    },
    id,
    now,
  );
};

/** Assigns a load to a room, or to none. A load belongs to at most one room. */
export const moveLoadToRoom = (
  config: RcuConfig,
  id: LoadId,
  roomId: RoomId | null,
  now: string,
): CommandResult<LoadId> => {
  const load = findLoad(config, id);
  if (load === undefined) return fail(loadNotFound(id));
  if (roomId !== null && findRoom(config, roomId) === undefined) return fail(roomMissing(roomId));

  return changedBy(
    {
      ...config,
      loads: config.loads.map((current) =>
        current.id === id ? { ...current, roomId } : current,
      ),
    },
    id,
    now,
  );
};
